import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MessageCircle, X, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { waApi } from '../services/api';
import { formatApiError, Spinner } from '../utils/helpers';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const MSG_TYPES = [
  { key: 'status_update', label: 'Update Status Pesanan' },
  { key: 'order_ready', label: 'Pesanan Siap Diambil' },
  { key: 'piutang_reminder', label: 'Tagihan Belum Lunas' },
  { key: 'dp_reminder', label: 'Reminder DP / Sisa Bayar' },
];

const STATUS_LABELS = {
  masuk: 'Masuk', dicuci: 'Sedang Dicuci', disetrika: 'Sedang Disetrika',
  siap_diambil: 'Siap Diambil ✅', selesai: 'Selesai ✅', selesai_lunas: 'Selesai & Lunas ✅',
  diagnosa: 'Dalam Diagnosa 🔍', sedang_dikerjakan: 'Sedang Dikerjakan 🔧',
  serah_terima: 'Siap Serah Terima ✅', konfirmasi: 'Dikonfirmasi',
  sedang_disewa: 'Sedang Disewa', dikembalikan: 'Dikembalikan ✅',
};

function fmtIdr(v) { try { return `Rp ${parseInt(v).toLocaleString('id-ID')}`; } catch { return `Rp ${v}`; } }

function fillTemplate(tpl, order, storeName) {
  if (!tpl || !order) return '';
  const ctx = {
    customer_name: order.customer_name || 'Pelanggan',
    tracking_code: order.tracking_code || '-',
    status_label: STATUS_LABELS[order.status] || order.status || '-',
    total_amount: fmtIdr(order.total_amount || 0),
    remaining_amount: fmtIdr(order.remaining_amount || 0),
    store_name: storeName || 'Toko',
    days: '',
  };
  let msg = tpl;
  Object.entries(ctx).forEach(([k, v]) => { msg = msg.replaceAll(`{${k}}`, v); });
  return msg + ``;
  //return msg + `\n\n_Pesan ini dikirim oleh ${storeName} via KasirWA.com_`;
}

export default function WaMessageModal({ order, defaultType = 'status_update', onClose, onSent }) {
  const { t } = useLanguage();
  const { user, store } = useAuth();
  const storeName = user?.store?.name || 'Toko';

  const [msgType, setMsgType] = useState(defaultType);
  const [customMsg, setCustomMsg] = useState('');
  const [editedMsg, setEditedMsg] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const { data: defaultTemplates = {} } = useQuery({
    queryKey: ['wa-default-templates'],
    queryFn: () => waApi.getDefaultTemplates().then(r => r.data),
    staleTime: 60000,
  });

  // Get effective template: store template > default template
  const storeTemplates = user?.store?.wa_settings?.templates || {};

  useEffect(() => {
    const tpl = storeTemplates[msgType] || defaultTemplates[msgType] || '';
    setEditedMsg(fillTemplate(tpl, order, storeName));
  }, [msgType, defaultTemplates]);

  const mutation = useMutation({
    mutationFn: () => waApi.sendOrder(order.id, msgType, editedMsg),
    onSuccess: (res) => {
      const fonnte = res.data?.response;
      if (fonnte?.status === false) {
        toast.error(`Gagal: ${fonnte?.reason || 'Token tidak valid / nomor salah'}`);
        return;
      }
      toast.success(`WA berhasil dikirim ke ${order.customer_phone}`);
      onSent?.();
      onClose();
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const phone = order?.customer_phone;
  const selectedLabel = MSG_TYPES.find(t => t.key === msgType)?.label || msgType;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center">
            <MessageCircle size={18} className="text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-slate-800 font-manrope">Kirim WhatsApp</h3>
            {phone
              ? <p className="text-xs text-slate-400 mt-0.5">ke {phone} ({order.customer_name})</p>
              : <p className="text-xs text-red-500 mt-0.5">Pelanggan tidak memiliki nomor WA</p>
            }
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {store?.is_demo_store && <div data-testid="demo-wa-simulation-notice" className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800"><strong>Simulasi Demo.</strong> Pesan akan tersimpan di WA Log untuk preview emulator, tetapi tidak dikirim ke WhatsApp asli.</div>}
          {/* Message type picker */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Jenis Pesan</label>
            <div className="relative">
              <button onClick={() => setShowTypeDropdown(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 hover:border-[#0f6c44] transition-colors">
                {selectedLabel}
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showTypeDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden">
                  {MSG_TYPES.map(opt => (
                    <button key={opt.key} onClick={() => { setMsgType(opt.key); setShowTypeDropdown(false); }}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 transition-colors ${msgType === opt.key ? 'text-[#0f6c44] font-semibold bg-emerald-50/50' : 'text-slate-700'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Message preview / editor */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Preview Pesan <span className="text-slate-400 font-normal text-xs">(bisa diedit)</span>
            </label>
            <textarea value={editedMsg} onChange={e => setEditedMsg(e.target.value)} rows={8}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] resize-none font-mono text-slate-700" />
            <p className="text-xs text-slate-400 mt-1">*teks* = tebal, _teks_ = miring di WhatsApp</p>
          </div>
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <button onClick={onClose} className="flex-1 h-11 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors">
            Batal
          </button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !phone || !editedMsg}
            data-testid="send-wa-button"
            className="flex-1 h-11 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {mutation.isPending
              ? <Spinner size="sm" className="border-white/30 border-t-white" />
              : <><Send size={16} />Kirim WA</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
